import React, { useMemo, useState } from 'react';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { icons } from './adminSidebarIcons';

const estadoPregunta = (value, enProceso = false) => {
  // El agente marca "En proceso" (propuesta activa) por encima del estado base.
  if (enProceso) return { label: 'En proceso', className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200' };
  if (Number(value) === 1) return { label: 'Aprobada', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200' };
  if (Number(value) === 0) return { label: 'Pendiente', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200' };
  if (Number(value) === -1) return { label: 'Rechazada', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200' };
  return { label: 'Revision', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200' };
};

const valorOrdenable = (pregunta, campo) => {
  if (campo === 'estado') return estadoPregunta(pregunta.aprobada, pregunta.en_proceso).label;
  return pregunta?.[campo] ?? '';
};

const QuestionManagementSection = ({ preguntas, isLoading, onRefresh, onEdit }) => {
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [limiteVisible, setLimiteVisible] = useState('20');
  const [orden, setOrden] = useState({ campo: null, direccion: null });
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: 'todas',
    dificultad: 'todas',
    estado: 'todas',
    autor: 'todos'
  });

  const categorias = useMemo(
    () => [...new Set(preguntas.map((item) => item.categoria).filter(Boolean))].sort(),
    [preguntas]
  );

  const dificultades = useMemo(
    () => [...new Set(preguntas.map((item) => item.dificultad).filter(Boolean))].sort(),
    [preguntas]
  );

  const preguntasFiltradas = useMemo(() => {
    const termino = filtros.busqueda.trim().toLowerCase();

    return preguntas.filter((pregunta) => {
      const estado = String(pregunta.aprobada ?? '');
      const autor = String(pregunta.autor || '').trim();
      const autorNormalizado = autor.toLowerCase();
      const esComunidad = autorNormalizado === 'comunidad';
      const esAnonimo = !autor || autorNormalizado === 'anonimo';

      const coincideBusqueda =
        !termino ||
        [
          pregunta.id,
          pregunta.pregunta,
          pregunta.respuesta_correcta,
          pregunta.categoria,
          pregunta.etiqueta,
          pregunta.autor
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(termino));

      const coincideCategoria = filtros.categoria === 'todas' || pregunta.categoria === filtros.categoria;
      const coincideDificultad = filtros.dificultad === 'todas' || pregunta.dificultad === filtros.dificultad;
      const coincideEstado =
        filtros.estado === 'todas' ||
        (filtros.estado === 'en_proceso' ? Boolean(pregunta.en_proceso) : estado === filtros.estado);

      const coincideAutor =
        filtros.autor === 'todos' ||
        (filtros.autor === 'comunidad' && esComunidad) ||
        (filtros.autor === 'anonimo' && esAnonimo) ||
        (filtros.autor === 'registrado' && !esComunidad && !esAnonimo);

      return coincideBusqueda && coincideCategoria && coincideDificultad && coincideEstado && coincideAutor;
    });
  }, [preguntas, filtros]);

  const preguntasOrdenadas = useMemo(() => {
    if (!orden.campo || !orden.direccion) {
      return preguntasFiltradas;
    }

    const factor = orden.direccion === 'asc' ? 1 : -1;

    return [...preguntasFiltradas].sort((a, b) => {
      const valorA = String(valorOrdenable(a, orden.campo) || '').trim();
      const valorB = String(valorOrdenable(b, orden.campo) || '').trim();
      return valorA.localeCompare(valorB, 'es', { sensitivity: 'base', numeric: true }) * factor;
    });
  }, [preguntasFiltradas, orden]);

  const preguntasMostradas = useMemo(() => {
    if (limiteVisible === 'all') {
      return preguntasOrdenadas;
    }

    const limite = Number(limiteVisible);
    if (!Number.isFinite(limite) || limite <= 0) {
      return preguntasOrdenadas;
    }

    return preguntasOrdenadas.slice(0, limite);
  }, [preguntasOrdenadas, limiteVisible]);

  const toggleOrden = (campo) => {
    setOrden((prev) => {
      if (prev.campo !== campo) return { campo, direccion: 'asc' };
      if (prev.direccion === 'asc') return { campo, direccion: 'desc' };
      return { campo: null, direccion: null };
    });
  };

  const claseHeaderOrden = (campo) => {
    const activo = orden.campo === campo;
    return `group inline-flex items-center gap-1.5 transition-colors ${activo ? 'text-blue-600 dark:text-blue-300' : 'text-inherit hover:text-blue-600 dark:hover:text-blue-300'}`;
  };

  const indicadorOrden = (campo) => {
    if (orden.campo !== campo) return <FaSort className="inline-block" aria-hidden="true" />;
    if (orden.direccion === 'asc') return <FaSortUp className="inline-block" aria-hidden="true" />;
    if (orden.direccion === 'desc') return <FaSortDown className="inline-block" aria-hidden="true" />;
    return <FaSort className="inline-block" aria-hidden="true" />;
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-xl">{icons['gestion-preguntas']}</span>
          Banco de Preguntas
        </h3>
        <button
          type="button"
          onClick={onRefresh}
          className="px-3 py-2 rounded-md border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
        >
          Actualizar lista
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-slate-50 via-white to-cyan-50 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800 p-4 sm:p-5 space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={filtros.busqueda}
            onChange={(event) => setFiltros((prev) => ({ ...prev, busqueda: event.target.value }))}
            placeholder="Buscar por texto, categoria, etiqueta, autor o ID"
            className="w-full px-3 py-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <button
            type="button"
            onClick={() => setMostrarFiltros((prev) => !prev)}
            className="shrink-0 h-[42px] w-[42px] rounded-md border border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            aria-label={mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
            title={mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className={`h-5 w-5 transition-transform duration-200 ${mostrarFiltros ? 'rotate-180' : ''}`}
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div
          className={`grid overflow-hidden transition-all duration-300 ease-in-out ${mostrarFiltros ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
          aria-hidden={!mostrarFiltros}
        >
          <div className="min-h-0">
            <div className="flex flex-wrap items-center gap-2 pt-1">

            <select
              value={filtros.categoria}
              onChange={(event) => setFiltros((prev) => ({ ...prev, categoria: event.target.value }))}
              className="w-auto min-w-[140px] px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="todas">Categoria: todas</option>
              {categorias.map((categoria) => (
                <option key={categoria} value={categoria}>{categoria}</option>
              ))}
            </select>

            <select
              value={filtros.dificultad}
              onChange={(event) => setFiltros((prev) => ({ ...prev, dificultad: event.target.value }))}
              className="w-auto min-w-[140px] px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="todas">Dificultad: todas</option>
              {dificultades.map((dificultad) => (
                <option key={dificultad} value={dificultad}>{dificultad}</option>
              ))}
            </select>

            <select
              value={filtros.estado}
              onChange={(event) => setFiltros((prev) => ({ ...prev, estado: event.target.value }))}
              className="w-auto min-w-[130px] px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="todas">Estado: todos</option>
              <option value="en_proceso">En proceso</option>
              <option value="1">Aprobadas</option>
              <option value="0">Pendientes</option>
              <option value="2">Revision</option>
              <option value="-1">Rechazadas</option>
            </select>

            <select
              value={filtros.autor}
              onChange={(event) => setFiltros((prev) => ({ ...prev, autor: event.target.value }))}
              className="w-auto min-w-[130px] px-2 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
            >
              <option value="todos">Autor: todos</option>
              <option value="comunidad">Comunidad</option>
              <option value="anonimo">Anonimo</option>
              <option value="registrado">Registrado</option>
            </select>

            <button
              type="button"
              onClick={() => setFiltros({ busqueda: '', categoria: 'todas', dificultad: 'todas', estado: 'todas', autor: 'todos' })}
              className="w-auto px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-xs hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              Limpiar filtros
            </button>
            </div>
          </div>
        </div>

        <div className="text-xs text-slate-600 dark:text-slate-300">
          <div className="flex items-center gap-2 w-full">
            <span>Mostrando {preguntasMostradas.length} de {preguntasOrdenadas.length} filtradas ({preguntas.length} totales)</span>
            <label className="inline-flex items-center gap-1.5 ml-auto">
              <span>Ver:</span>
              <select
                value={limiteVisible}
                onChange={(event) => setLimiteVisible(event.target.value)}
                className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="all">Todas</option>
              </select>
            </label>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-900 flex-1 min-h-0 flex flex-col">
        <div className="hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0 overflow-y-auto overscroll-contain overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: '32%' }} />
                <col style={{ width: '23%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-10 shadow-[0_1px_0_0_rgb(226_232_240)] dark:shadow-[0_1px_0_0_rgb(51_65_85)]">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('pregunta')} onClick={() => toggleOrden('pregunta')}>
                      Pregunta <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('pregunta')}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('respuesta_correcta')} onClick={() => toggleOrden('respuesta_correcta')}>
                      Respuesta correcta <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('respuesta_correcta')}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('categoria')} onClick={() => toggleOrden('categoria')}>
                      Categoria <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('categoria')}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('etiqueta')} onClick={() => toggleOrden('etiqueta')}>
                      Etiqueta <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('etiqueta')}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('dificultad')} onClick={() => toggleOrden('dificultad')}>
                      Dificultad <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('dificultad')}</span>
                    </button>
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold">
                    <button type="button" className={claseHeaderOrden('estado')} onClick={() => toggleOrden('estado')}>
                      Estado <span className="text-xs opacity-70 group-hover:opacity-100">{indicadorOrden('estado')}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-center text-slate-500 dark:text-slate-300">
                    Cargando preguntas...
                  </td>
                </tr>
              )}

              {!isLoading && preguntasOrdenadas.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-3 py-4 text-center text-slate-500 dark:text-slate-300">
                    No hay preguntas para mostrar con los filtros actuales.
                  </td>
                </tr>
              )}

              {!isLoading && preguntasMostradas.map((pregunta) => {
                const estado = estadoPregunta(pregunta.aprobada, pregunta.en_proceso);

                return (
                  <tr
                    key={pregunta.id}
                    className="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/70 cursor-pointer"
                    onClick={() => onEdit(pregunta)}
                  >
                    <td className="px-3 py-2.5 max-w-[380px]">
                      <p className="line-clamp-2 font-medium text-slate-800 dark:text-slate-100">{pregunta.pregunta || '-'}</p>
                    </td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <p className="line-clamp-2 text-slate-700 dark:text-slate-200">{pregunta.respuesta_correcta || '-'}</p>
                    </td>
                    <td className="px-3 py-2.5">{pregunta.categoria || '-'}</td>
                    <td className="px-3 py-2.5">{pregunta.etiqueta || '-'}</td>
                    <td className="px-3 py-2.5">{pregunta.dificultad || '-'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${estado.className}`}>
                        {estado.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              </tbody>
            </table>
        </div>

        <div className="lg:hidden flex-1 min-h-0 overflow-y-auto overscroll-contain overflow-x-auto">
          <table className="min-w-full w-full text-xs table-fixed">
            <colgroup>
              <col style={{ width: '48%' }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: '18%' }} />
            </colgroup>
            <thead className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800">
              <tr>
                <th className="text-left px-2 py-2 font-semibold">
                  <button type="button" className={claseHeaderOrden('pregunta')} onClick={() => toggleOrden('pregunta')}>
                    Pregunta <span className="text-[10px] opacity-70 group-hover:opacity-100">{indicadorOrden('pregunta')}</span>
                  </button>
                </th>
                <th className="text-left px-2 py-2 font-semibold">
                  <button type="button" className={claseHeaderOrden('respuesta_correcta')} onClick={() => toggleOrden('respuesta_correcta')}>
                    Respuesta <span className="text-[10px] opacity-70 group-hover:opacity-100">{indicadorOrden('respuesta_correcta')}</span>
                  </button>
                </th>
                <th className="text-left px-2 py-2 font-semibold">
                  <button type="button" className={claseHeaderOrden('estado')} onClick={() => toggleOrden('estado')}>
                    Estado <span className="text-[10px] opacity-70 group-hover:opacity-100">{indicadorOrden('estado')}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan="3" className="px-2 py-3 text-center text-slate-500 dark:text-slate-300">
                    Cargando preguntas...
                  </td>
                </tr>
              )}

              {!isLoading && preguntasOrdenadas.length === 0 && (
                <tr>
                  <td colSpan="3" className="px-2 py-3 text-center text-slate-500 dark:text-slate-300">
                    No hay preguntas para mostrar con los filtros actuales.
                  </td>
                </tr>
              )}

              {!isLoading && preguntasMostradas.map((pregunta) => {
                const estado = estadoPregunta(pregunta.aprobada, pregunta.en_proceso);

                return (
                  <tr
                    key={pregunta.id}
                    className="border-t border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/70 cursor-pointer"
                    onClick={() => onEdit(pregunta)}
                  >
                    <td className="px-2 py-2 align-top">
                      <p className="line-clamp-3 text-slate-800 dark:text-slate-100">{pregunta.pregunta || '-'}</p>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <p className="line-clamp-3 text-slate-700 dark:text-slate-200">{pregunta.respuesta_correcta || '-'}</p>
                    </td>
                    <td className="px-2 py-2 align-top">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${estado.className}`}>
                        {estado.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuestionManagementSection;
